import { MergeTag } from 'api/mergeTag'

// Unlayer email-editor merge-tags map. Previously imported from the
// non-existent `state/types/types` module (a broken type-only import that the
// transpile-only dev build tolerated but the type-checker flagged); defined
// locally to match the shape this file builds and Unlayer consumes.
type UnlayerMergeTags = Record<
  string,
  { name: string; value: string; sample: string }
>

// The default email templates use these tags for the merchant's name, logo and
// cover, so they resolve when the email is sent rather than when it's designed.
const SYSTEM_MERGE_TAG_TYPES = [
  'merchant_name',
  'header_image_url',
  'hero_image_url',
]

export type MergeTagItem = {
  id: number
  mergeTagType: string
  defaultValue: string
}

export const getMergeTagTypeName = (mergeTagType: string) => {
  if (!mergeTagType) {
    return ''
  }
  return mergeTagType
    .replace(/_/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase())
}

export const getCurrentMergeTagsMap = (
  currentMergeTagsMap: Map<number, MergeTagItem> = new Map(),
  newMergeTags: MergeTag[]
): Map<number, MergeTagItem> => {
  newMergeTags.forEach(newMergeTag => {
    const currentMergeTagItem = currentMergeTagsMap.get(newMergeTag.id)
    if (currentMergeTagItem) {
      currentMergeTagItem.defaultValue =
        currentMergeTagItem.defaultValue || newMergeTag.default_value
      currentMergeTagsMap.set(newMergeTag.id, currentMergeTagItem)
      return
    }
    const newMergeTagItem: MergeTagItem = {
      id: newMergeTag.id,
      mergeTagType: newMergeTag.merge_tag_type,
      defaultValue: newMergeTag.default_value,
    }
    currentMergeTagsMap.set(newMergeTag.id, newMergeTagItem)
  })

  return new Map<number, MergeTagItem>(currentMergeTagsMap)
}

export const getUnlayerMergeTags = (
  mergeTags?: MergeTag[]
): UnlayerMergeTags | undefined => {
  if (!mergeTags || !mergeTags.length) {
    return
  }

  const unlayerMergeTags: UnlayerMergeTags = {}
  mergeTags.forEach(({ merge_tag_type, default_value }) => {
    const name = getMergeTagTypeName(merge_tag_type)
    unlayerMergeTags[merge_tag_type] = {
      name,
      value: `{{${merge_tag_type}}}`,
      sample: default_value,
    }
  })

  return unlayerMergeTags
}

// Samples come from the design variables, which hold the merchant's current
// values, so the editor shows the real name and images in place of the tags.
export const getSystemUnlayerMergeTags = (
  designVariables: { name: string; value: string }[]
): UnlayerMergeTags => {
  const unlayerMergeTags: UnlayerMergeTags = {}
  SYSTEM_MERGE_TAG_TYPES.forEach(mergeTagType => {
    const designVariable = designVariables.find(
      ({ name }) => name === mergeTagType
    )
    unlayerMergeTags[mergeTagType] = {
      name: getMergeTagTypeName(mergeTagType),
      value: `{{${mergeTagType}}}`,
      sample: designVariable?.value ?? '',
    }
  })

  return unlayerMergeTags
}
